import { Tooltip } from 'antd';
import moment from 'moment';
import React, { useMemo } from 'react';
import { FormattedMessage } from 'react-intl';
import styled from 'styled-components';
import {
  CpBody01Primary,
  CpBody03Primary,
} from 'components/atoms/customer-portal/typography';
import { ArrElement } from 'utils/arrays';
import { useGetBackground, useGetStyle } from '../types/utils';
import { CpWidgetProps } from '../types/widget';
import {
  CpSectionWrapper,
  CpWidgetDescription,
  CpWidgetTitle,
  PaddingSize,
} from './common';
import {
  SummaryInfoCard,
  SummaryInfoCardLabel,
  SummaryInfoCardValue,
  SummaryInfoCardWrapper,
} from './cp-summary-information/cp-summary-information';

interface CpProjectOverviewData extends Record<string, unknown> {
  title: string;
  description?: string;
  status: {
    label: string;
    value: number;
  };
  projectPhases: {
    projectPhaseName: string;
    projectPhaseId: number;
    inProgressTaskCount: number;
    totalTaskCount: number;
    completedTaskCount: number;
    startDate: string;
    dueDate: string;
  }[];
  completionPercentage: number;
  startDate: string;
  dueDate: string;
}

const CardsWrapper = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: var(--cp-spacing-06);
  align-items: start;
  margin-top: var(--cp-spacing-06);

  @media (max-width: 480px) {
    margin-top: var(--cp-spacing-05);
    flex-direction: column;
    gap: var(--cp-spacing-05);
  }
`;

const PhaseListWrapper = styled.div`
  display: flex;
  flex-direction: column;
  gap: var(--cp-spacing-06);
  margin-top: var(--cp-spacing-10);

  @media (max-width: 480px) {
    margin-top: var(--cp-spacing-07);
    gap: var(--cp-spacing-05);
  }
`;

const PhaseCard = styled.div`
  width: 100%;
  height: 100%;

  display: flex;
  align-items: start;
  justify-content: end;
  flex-direction: column;
  margin-bottom: var(--cp-spacing-07);
  .rl-cp-project-overview-phase-name {
    margin-bottom: var(--cp-spacing-03);
  }

  @media (max-width: 480px) {
    margin-bottom: var(--cp-spacing-05);
  }
`;

const ProgressBarWrapper = styled.div`
  width: 100%;
  height: 40px;
  display: flex;
  align-items: center;
  justify-content: start;
  background-color: var(--cp-background-as-white-opacity);
  .cp-light-background &,
  .cp-very-light-background & {
    background-color: var(--cp-background-as-black-opacity);
  }
  position: relative;
`;

const CompletedProgressBar = styled.div<{ percentage: number }>`
  width: ${({ percentage }) => `${percentage}%`};
  height: 100%;
  background-color: var(--cp-green-40);
`;

const InProgressProgressBar = styled.div<{ percentage: number }>`
  width: ${({ percentage }) => `${percentage}%`};
  height: 100%;
  background-color: var(--cp-green-20);
`;

const StartDate = styled(CpBody01Primary)`
  position: absolute;
  top: 50%;
  transform: translateY(-50%);
  left: var(--cp-spacing-06);

  @media (max-width: 480px) {
    left: var(--cp-spacing-04);
  }
`;

const DueDate = styled(CpBody01Primary)`
  position: absolute;
  top: 50%;
  transform: translateY(-50%);
  right: var(--cp-spacing-06);

  @media (max-width: 480px) {
    right: var(--cp-spacing-04);
  }
`;

const ProgressBar: React.FC<{
  completedPercentage: number;
  inProgressPercentage: number;
  startDate: string;
  dueDate: string;
}> = ({ completedPercentage, inProgressPercentage, startDate, dueDate }) => {
  return (
    <ProgressBarWrapper>
      <CompletedProgressBar percentage={completedPercentage} />
      <InProgressProgressBar percentage={inProgressPercentage} />
      <StartDate autoAdjustColor>
        {moment(startDate).format('DD MMM YYYY')}
      </StartDate>
      <DueDate autoAdjustColor>{moment(dueDate).format('DD MMM YYYY')}</DueDate>
    </ProgressBarWrapper>
  );
};

const Phase: React.FC<ArrElement<CpProjectOverviewData['projectPhases']>> = ({
  projectPhaseName,
  inProgressTaskCount,
  totalTaskCount,
  completedTaskCount,
  startDate,
  dueDate,
}) => {
  const completedPercentage = useMemo(() => {
    if (!totalTaskCount) {
      return 0;
    }

    return (completedTaskCount / totalTaskCount) * 100;
  }, [completedTaskCount, totalTaskCount]);

  const inProgressPercentage = useMemo(() => {
    if (!totalTaskCount) {
      return 0;
    }

    return (inProgressTaskCount / totalTaskCount) * 100;
  }, [inProgressTaskCount, totalTaskCount]);

  return (
    <PhaseCard>
      <CpBody03Primary
        autoAdjustColor
        className="rl-cp-project-overview-phase-name"
      >
        {projectPhaseName}
      </CpBody03Primary>
      <ProgressBar
        completedPercentage={completedPercentage}
        inProgressPercentage={inProgressPercentage}
        startDate={startDate}
        dueDate={dueDate}
      />
    </PhaseCard>
  );
};

export const CpProjectOverView: React.FC<CpWidgetProps> = ({
  widget,
  disabled,
  data: _data,
}) => {
  const bg = useGetBackground(widget);
  const paddingY = useGetStyle(widget, 'paddingY', PaddingSize.Medium);
  const data = useMemo(() => _data as CpProjectOverviewData, [_data]);

  const formattedProjectDates = useMemo(() => {
    if (!data.startDate && !data.dueDate) {
      return '-';
    }

    if (!data.startDate) {
      return moment(data.dueDate).format('DD MMM YYYY');
    }

    if (!data.dueDate) {
      return moment(data.startDate).format('DD MMM YYYY');
    }

    return `${moment(data.startDate).format('DD MMM YYYY')} - ${moment(
      data.dueDate,
    ).format('DD MMM YYYY')}`;
  }, [data.startDate, data.dueDate]);

  const completionPercentage = useMemo(() => {
    if (!data.completionPercentage) {
      return '-';
    }

    return Math.round(data.completionPercentage);
  }, [data.completionPercentage]);

  return (
    <CpSectionWrapper background={bg} paddingY={paddingY} isDisabled={disabled}>
      <CpWidgetTitle autoAdjustColor>{data.title}</CpWidgetTitle>
      <CpWidgetDescription autoAdjustColor>
        {data.description}
      </CpWidgetDescription>
      <CardsWrapper>
        <SummaryInfoCardWrapper>
          <SummaryInfoCard>
            <SummaryInfoCardLabel>
              <FormattedMessage id="status" />
            </SummaryInfoCardLabel>

            <Tooltip title={data?.status?.label} placement="topLeft">
              <SummaryInfoCardValue>
                {data?.status?.label ?? '-'}
              </SummaryInfoCardValue>
            </Tooltip>
          </SummaryInfoCard>
        </SummaryInfoCardWrapper>

        <SummaryInfoCardWrapper>
          <SummaryInfoCard>
            <SummaryInfoCardLabel>
              <FormattedMessage id="project_dates" />
            </SummaryInfoCardLabel>
            <Tooltip title={formattedProjectDates} placement="topLeft">
              <SummaryInfoCardValue>
                {formattedProjectDates}
              </SummaryInfoCardValue>
            </Tooltip>
          </SummaryInfoCard>
        </SummaryInfoCardWrapper>

        <SummaryInfoCardWrapper>
          <SummaryInfoCard>
            <SummaryInfoCardLabel>
              <FormattedMessage id="completion" />
            </SummaryInfoCardLabel>
            <Tooltip title={`${completionPercentage}%`} placement="topLeft">
              <SummaryInfoCardValue>
                {completionPercentage && completionPercentage !== '-'
                  ? `${completionPercentage}%`
                  : '-'}
              </SummaryInfoCardValue>
            </Tooltip>
          </SummaryInfoCard>
        </SummaryInfoCardWrapper>
      </CardsWrapper>
      <PhaseListWrapper>
        {data.projectPhases?.map?.((phase) => (
          <Phase key={phase.projectPhaseId} {...phase} />
        ))}
      </PhaseListWrapper>
    </CpSectionWrapper>
  );
};
